import { useEffect } from 'react';
import { useSharedValue, useAnimatedStyle, withRepeat, withTiming, cancelAnimation, Easing, withSequence } from 'react-native-reanimated';
import { AnimationConfig } from '@interfaces/base';

interface UseManageActiveMachineAnimatedIconParams extends Required<AnimationConfig> {
  endOpacity: number;
}

const useManageActiveMachineAnimatedIcon = ({
  duration,
  minScale,
  maxScale,
  startOpacity,
  endOpacity,
}: UseManageActiveMachineAnimatedIconParams) => {
  const scale = useSharedValue(maxScale);
  const opacity = useSharedValue(endOpacity);

  const halfDuration = duration / 2;

  useEffect(() => {
    // Calculate the current phase based on global time to synchronize with other animations
    const currentPhase = (Date.now() % duration) / duration;

    // Determine if we're in first half (max->min) or second half (min->max)
    const isFirstHalf = currentPhase < 0.5;
    const phaseInHalf = isFirstHalf ? currentPhase * 2 : (currentPhase - 0.5) * 2;

    // Calculate current values based on phase
    const currentScale = isFirstHalf
      ? maxScale - phaseInHalf * (maxScale - minScale)
      : minScale + phaseInHalf * (maxScale - minScale);

    const currentOpacity = isFirstHalf
      ? endOpacity - phaseInHalf * (endOpacity - startOpacity)
      : startOpacity + phaseInHalf * (endOpacity - startOpacity);

    scale.value = currentScale;
    opacity.value = currentOpacity;

    // Calculate remaining time in current half
    const remainingInHalf = halfDuration * (1 - phaseInHalf);

    if (isFirstHalf) {
      // Complete first half (max -> min), then start full cycle
      scale.value = withTiming(minScale, { duration: remainingInHalf, easing: Easing.out(Easing.ease) }, () => {
        scale.value = withRepeat(
          withSequence(
            withTiming(maxScale, { duration: halfDuration, easing: Easing.out(Easing.ease) }),
            withTiming(minScale, { duration: halfDuration, easing: Easing.out(Easing.ease) })
          ),
          -1,
          false
        );
      });
      opacity.value = withTiming(startOpacity, { duration: remainingInHalf, easing: Easing.out(Easing.ease) }, () => {
        opacity.value = withRepeat(
          withSequence(
            withTiming(endOpacity, { duration: halfDuration, easing: Easing.out(Easing.ease) }),
            withTiming(startOpacity, { duration: halfDuration, easing: Easing.out(Easing.ease) })
          ),
          -1,
          false
        );
      });
    } else {
      // Complete second half (min -> max), then start full cycle
      scale.value = withTiming(maxScale, { duration: remainingInHalf, easing: Easing.out(Easing.ease) }, () => {
        scale.value = withRepeat(
          withSequence(
            withTiming(minScale, { duration: halfDuration, easing: Easing.out(Easing.ease) }),
            withTiming(maxScale, { duration: halfDuration, easing: Easing.out(Easing.ease) })
          ),
          -1,
          false
        );
      });
      opacity.value = withTiming(endOpacity, { duration: remainingInHalf, easing: Easing.out(Easing.ease) }, () => {
        opacity.value = withRepeat(
          withSequence(
            withTiming(startOpacity, { duration: halfDuration, easing: Easing.out(Easing.ease) }),
            withTiming(endOpacity, { duration: halfDuration, easing: Easing.out(Easing.ease) })
          ),
          -1,
          false
        );
      });
    }

    return () => {
      cancelAnimation(scale);
      cancelAnimation(opacity);
    };
  }, [duration, minScale, maxScale, startOpacity, endOpacity]);

  const animatedCenterDotStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  return { animatedCenterDotStyle };
};

export { useManageActiveMachineAnimatedIcon };
